import React, { useState, useEffect, useCallback } from 'react';
import { Bell } from 'lucide-react';
import Pusher from 'pusher-js';
import styles from './Notifications.module.css';
import NotificationsPanel from './NotificationsPanel';
import { authenticatedFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const NotificationsIcon = () => {
    const [showPanel, setShowPanel] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuth();

    const processNotification = (notification) => {
        return {
            id: notification._id,
            read: notification.read,
            timestamp: notification.created_at,
            responded: notification.responded,
            type: notification.type,
            ...notification.data
        };
    };

    const fetchNotifications = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await authenticatedFetch('/api/notifications/list');
            if (response) {
                setNotifications(response.notifications.map(processNotification) || []);
                setUnreadCount(response.unreadCount || 0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (showPanel) {
            const handleClickOutside = (event) => {
                if (!event.target.closest(`.${styles.notificationsContainer}`)) {
                    setShowPanel(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPanel]);

    useEffect(() => {
        if (!user?.id) return;

        if (!initialFetchDone) {
            fetchNotifications();
            setInitialFetchDone(true);
        }

        const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
            cluster: process.env.REACT_APP_PUSHER_CLUSTER
        });

        const channel = pusher.subscribe(`user-notifications-${user.id}`);

        channel.bind('new-notification', notification => {
            const processedNotification = processNotification(notification);
            setNotifications(prev => [processedNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
        });

        return () => {
            channel.unbind_all();
            channel.unsubscribe();
            pusher.disconnect();
        };
    }, [fetchNotifications, initialFetchDone, user?.id]);

    const handleClick = () => {
        setShowPanel(!showPanel);
        if (!showPanel) {
            fetchNotifications();
        }
        if (unreadCount > 0) {
            markAsRead();
        }
    };

    const markAsRead = async () => {
        try {
            await authenticatedFetch('/api/notifications/mark-read', {
                method: 'POST'
            });
            setUnreadCount(0);
            setNotifications(prev =>
                prev.map(notification => ({
                    ...notification,
                    read: true
                }))
            );
        } catch (error) {
            console.error('Error marking notifications as read:', error);
        }
    };

    if (!user) return null;

    return (
        <div className={styles.notificationsContainer}>
            <button className={styles.iconButton} onClick={handleClick}>
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className={styles.badge}>{unreadCount}</span>
                )}
            </button>
            <NotificationsPanel
                notifications={notifications}
                onClose={() => setShowPanel(false)}
                onUpdate={fetchNotifications}
                isLoading={isLoading}
                isVisible={showPanel}
            />
        </div>
    );
};

export default NotificationsIcon;
